import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ResourcePool } from '@claude-code-api/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { BuyCreditsDialog } from '@/components/payments/BuyCreditsDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface UsageSubscriptionData {
  subscription: {
    status: string;
    currentPeriodEnd: string;
    creditsCharged: number;
    autoRenew: boolean;
    stripeAutoChargeEnabled?: boolean;
    subscriptionCreditBalance?: number;
    purchasedCreditBalance?: number;
    totalCreditBalance?: number;
    plan: {
      id: string;
      name: string;
      cpuCores: number;
      memoryGb: number;
      storageGb: number;
      monthlyCredits: number;
      monthlyPriceUsd: number;
    } | null;
  } | null;
  creditBalances?: {
    subscriptionCreditBalance: number;
    purchasedCreditBalance: number;
    totalCreditBalance: number;
  };
  pool: ResourcePool;
}

function ResourceBar({
  label,
  used,
  total,
  unit,
}: {
  label: string;
  used: number;
  total: number;
  unit: string;
}) {
  const { t } = useTranslation('usage');
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const roundedPct = Math.round(pct);
  const remaining = Math.max(0, total - used);
  const color =
    pct > 90 ? 'bg-destructive' : pct > 70 ? 'bg-warning' : 'bg-primary';
  const formatValue = (value: number) =>
    value.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {used} / {total} {unit}
        </span>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="h-2 cursor-help overflow-hidden rounded-full bg-muted outline-none ring-offset-background transition-shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            role="progressbar"
            tabIndex={0}
            aria-label={`${label}: ${roundedPct}%`}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={Math.min(used, total)}
          >
            <div
              className={`h-full rounded-full transition-all ${color}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent className="w-52 space-y-2 border bg-popover text-popover-foreground shadow-md">
          <div className="flex items-center justify-between gap-4">
            <p className="font-semibold">{label}</p>
            <span className="font-mono text-muted-foreground">
              {roundedPct}%
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted-foreground">{t('resourceUsed')}</dt>
            <dd className="text-right font-medium">
              {formatValue(used)} {unit}
            </dd>
            <dt className="text-muted-foreground">{t('resourceRemaining')}</dt>
            <dd className="text-right font-medium">
              {formatValue(remaining)} {unit}
            </dd>
            <dt className="text-muted-foreground">{t('resourceLimit')}</dt>
            <dd className="text-right font-medium">
              {formatValue(total)} {unit}
            </dd>
          </dl>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export default function UsagePage() {
  const { t } = useTranslation(['usage', 'subscription']);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] =
    useState<UsageSubscriptionData | null>(null);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [purchaseReason, setPurchaseReason] = useState<string | null>(null);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  useEffect(() => {
    void fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const subRes = await fetch('/api/subscriptions/me', {
        credentials: 'include',
      });

      if (subRes.ok) {
        const subData = await subRes.json();
        const normalized = subData.data || null;
        setSubscriptionData(normalized);
        const isActive = normalized?.subscription?.status === 'active';
        setSubscriptionActive(Boolean(isActive));
        setPurchaseReason(isActive ? null : t('usage:messages.inactiveReason'));
      }
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeState = params.get('stripe');
    const sessionId = params.get('session_id');
    if (stripeState === 'credit_cancel') {
      toast.message(t('usage:messages.checkoutCancelled'));
      params.delete('stripe');
      params.delete('session_id');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', next);
      return;
    }
    if (stripeState !== 'credit_success' || !sessionId) return;

    const confirm = async () => {
      try {
        const res = await fetch('/api/credits/packs/checkout/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ sessionId }),
        });
        const result = await res.json();
        if (!res.ok) {
          toast.error(result.error || t('usage:messages.confirmFailed'));
          return;
        }
        toast.success(t('usage:messages.confirmSuccess'));
        await fetchData();
      } catch {
        toast.error(t('usage:messages.confirmFailed'));
      } finally {
        params.delete('stripe');
        params.delete('session_id');
        const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, '', next);
      }
    };

    void confirm();
  }, [t]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('usage:title')}
          description={t('usage:description')}
        />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const sub = subscriptionData?.subscription;
  const balances = subscriptionData?.creditBalances || null;
  const pool = subscriptionData?.pool;
  const isActive = sub?.status === 'active';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={t('usage:title')}
          description={t('usage:description')}
        />
        <Button
          variant="outline"
          onClick={() => setBuyCreditsOpen(true)}
          disabled={!subscriptionActive}
        >
          {t('usage:buyCredits')}
        </Button>
      </div>

      {sub ? (
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('subscription:currentPlan.title')}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <h2 className="text-2xl font-semibold">
                  {sub.plan?.name || t('subscription:currentPlan.unknown')}
                </h2>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-destructive/10 text-destructive'}`}
                >
                  {sub.status}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('subscription:description')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" asChild>
                <Link to="/billing/subscription">{t('nav:subscription')}</Link>
              </Button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-4 xl:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">
                {t('subscription:currentPlan.monthlyCost')}
              </p>
              <p className="mt-1 text-xl font-bold">
                {t('subscription:creditsUnit', {
                  count: Math.round(Number(sub.creditsCharged)),
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('subscription:currentPlan.nextBilling')}
              </p>
              <p className="mt-1 text-xl font-bold">
                {new Date(sub.currentPeriodEnd).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('subscription:currentPlan.autoRenew')}
              </p>
              <p className="mt-1 text-xl font-bold">
                {sub.autoRenew
                  ? t('subscription:currentPlan.yes')
                  : t('subscription:currentPlan.no')}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('subscription:currentPlan.savedCard')}
              </p>
              <p className="mt-1 text-xl font-bold">
                {sub.stripeAutoChargeEnabled
                  ? t('subscription:currentPlan.ready')
                  : t('subscription:currentPlan.notSet')}
              </p>
            </div>
          </div>

          <div className="mt-5 rounded-xl border bg-surface-0 p-4">
            <p className="text-sm font-medium">
              {t('subscription:currentPlan.creditWallets')}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
              <div>
                <p className="text-muted-foreground">
                  {t('subscription:currentPlan.subscriptionCredits')}
                </p>
                <p className="font-semibold">
                  {Math.round(
                    Number(
                      balances?.subscriptionCreditBalance ??
                        sub.subscriptionCreditBalance ??
                        0,
                    ),
                  ).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">
                  {t('subscription:currentPlan.purchasedCredits')}
                </p>
                <p className="font-semibold">
                  {Math.round(
                    Number(
                      balances?.purchasedCreditBalance ??
                        sub.purchasedCreditBalance ??
                        0,
                    ),
                  ).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">
                  {t('subscription:currentPlan.totalCredits')}
                </p>
                <p className="font-semibold">
                  {Math.round(
                    Number(
                      balances?.totalCreditBalance ??
                        sub.totalCreditBalance ??
                        0,
                    ),
                  ).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {pool ? (
            <TooltipProvider delayDuration={150}>
              <div className="mt-5 space-y-3">
                <ResourceBar
                  label={t('subscription:resources.cpu')}
                  used={pool.used.cpuCores}
                  total={pool.effectiveLimits.cpuCores}
                  unit={t('subscription:resources.cores')}
                />
                <ResourceBar
                  label={t('subscription:resources.memory')}
                  used={pool.used.memoryGb}
                  total={pool.effectiveLimits.memoryGb}
                  unit={t('subscription:resources.gb')}
                />
                <ResourceBar
                  label={t('subscription:resources.storage')}
                  used={pool.used.storageGb}
                  total={pool.effectiveLimits.storageGb}
                  unit={t('subscription:resources.gb')}
                />
              </div>
            </TooltipProvider>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-base font-semibold">{t('usage:historyTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('usage:historyDescription')}
          </p>
        </div>
        <Button asChild variant="outline" className="shrink-0">
          <Link to="/billing/history">{t('usage:viewFullHistory')}</Link>
        </Button>
      </div>

      <BuyCreditsDialog
        open={buyCreditsOpen}
        onOpenChange={setBuyCreditsOpen}
        canPurchase={subscriptionActive}
        reason={purchaseReason}
        returnPath={window.location.pathname}
      />
    </div>
  );
}
